import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import {reporter} from 'io-ts-reporters';
import {isString} from 'lodash';

import {
  annotateTaskTiming,
  TaskSourcesEnum,
  TaskTimingTypesEnum,
} from '../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {CustomError} from '../../libs/shared/helpers/src/types/customErrors';
import {ApiType, ModelType} from '../models/api/apiTypes';
import {inIdleOrHiddenCallbackAsync} from './async/inIdleCallback';

/*
 * Model parsing.
 */

/** Validate that a plain JS object conforms to the schema described in the specified {@link IApiType} */
export function readApiModel<T extends t.Mixed>(apiType: ApiType<T>, src: any): ModelType<T> {
  const startTime = performance.now();

  ensureParseableApiResponse(src);

  // Parse the object.
  const modelParsed = apiType.parse(src);

  // Validate it.
  const validationResult = apiType.runtimeType.decode(modelParsed);
  const modelValidated = ensureValidApiModel(apiType, src, validationResult);

  annotateTaskTiming(TaskTimingTypesEnum.IOTS, startTime, performance.now() - startTime, apiType.name);

  return modelValidated;
}

export async function readApiModelInIdleCallbackAsync<T extends t.Mixed>(apiType: ApiType<T>, src: any) {
  ensureParseableApiResponse(src);

  // Parse the object
  const modelParsed = await inIdleOrHiddenCallbackAsync(() => apiType.parse(src), {
    source: TaskSourcesEnum.IOTS,
  });

  // Validate it.
  const modelValidated = await inIdleOrHiddenCallbackAsync(() => apiType.runtimeType.decode(modelParsed), {
    source: TaskSourcesEnum.IOTS,
  });

  return ensureValidApiModel(apiType, src, modelValidated);
}

function ensureParseableApiResponse(src: any) {
  const isHtmlResponse = isString(src) && src.includes('<html');
  if (isHtmlResponse) {
    throw new ApiBadResponseError(src);
  }
}

function ensureValidApiModel<T extends t.Mixed>(
  apiType: ApiType<T>,
  src: any,
  modelValidated: E.Either<t.Errors, any>,
) {
  const onLeft = () => {
    reporter(modelValidated).forEach((err) => {
      throw new IOTSError(apiType.name, err, JSON.stringify(src, null, 2).slice(0, 10000));
    });
    throw new Error('[io-ts] This error should not be reached.');
  };
  return E.getOrElse<t.Errors, any>(onLeft)(modelValidated);
}

/*
 * Error handling.
 */

export class IOTSError extends CustomError {
  constructor(modelName: string, decodeError: string, rawModel?: string) {
    super(`[io-ts] Error parsing the model: ${modelName}: ${decodeError}`);

    this.modelName = modelName;
    this.decodeError = decodeError;
    this.rawModel = rawModel;
  }

  get groupingHash() {
    return `io-ts-${this.modelName}`;
  }

  readonly modelName: string;
  readonly decodeError: string;
  readonly rawModel?: string;
}

export class ApiBadResponseError extends CustomError {
  constructor(body?: any) {
    super('[io-ts] Bad API response');

    this.body = JSON.stringify(body, null, 2)?.slice(0, 1500);
  }

  readonly body: string;
}
